import { SkipLink } from "@kds-web/skip-link";
import clsx from "clsx";
import { useEffect, useState, useRef } from "react";
import type { JSX } from "react/jsx-runtime";
import type { HomepageCategory } from "../../../../actions/homePage/homepageCategories.ts";
import { LinkButton } from "../../../common/LinkButton.tsx";

type Props = {
  categories: HomepageCategory[];
};

function CategoryTreeList({ categories }: Props): JSX.Element {
  const [expandedCategory, setExpandedCategory] = useState<HomepageCategory>();
  const linkRefs = useRef<Map<string, HTMLAnchorElement | null>>(new Map());

  useEffect(() => {
    function focusFirstExpandedLink(): void {
      const initiallyVisibleCount = expandedCategory?.children.filter(
        (child) => child.visible,
      ).length;
      const refKey = `${expandedCategory?.categoryId}-${initiallyVisibleCount}`;
      linkRefs.current.get(refKey)?.focus();
    }

    if (expandedCategory) {
      focusFirstExpandedLink();
    }
  }, [expandedCategory]);

  return (
    <div className="relative">
      <SkipLink
        label="Liste überspringen"
        targetId="homepage-main"
        className="top-small"
      />
      {/* It is needed to set the `role="list"` to ensure Safari keeps their semantic meaning:
          https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul
        className="m-none list-none overflow-auto px-xxsmall py-none"
        role="list"
      >
        {categories.map((category, index) => {
          const isCategoryExpanded =
            category.categoryId === expandedCategory?.categoryId;
          const hasInvisibleChildren = category.children.some(
            (child) => !child.visible,
          );
          const showMoreButton = !isCategoryExpanded && hasInvisibleChildren;

          return (
            <li
              key={category.localizedCategoryName}
              className={clsx(
                "border-utilityNonessential px-none py-xxsmall",
                index === 0 ? "border-t-0" : "border-t",
                category.cssClassForHighlightedItem,
              )}
            >
              <a
                className="inline-block py-[5px] text-bodySmallStrong text-onSurfaceSubdued hover:underline"
                href={category.seoUrl}
                id={`category-${category.categoryId}`}
              >
                {category.localizedCategoryName}
              </a>
              {/* It is needed to set the `role="list"` to ensure Safari keeps their semantic meaning: */}
              {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
              <ul className="list-none pl-small" role="list">
                {category.children
                  .filter((child) => child.visible || isCategoryExpanded)
                  .map((child, childIndex) => {
                    const refKey = `${category.categoryId}-${childIndex}`;
                    return (
                      <li
                        key={child.localizedCategoryName}
                        className={category.cssClassForHighlightedItem}
                      >
                        <a
                          className="inline-flex min-h-large items-center text-bodySmall font-regular text-onSurfaceSubdued hover:underline"
                          href={child.seoUrl}
                          ref={(el) => {
                            if (el) {
                              linkRefs.current.set(refKey, el);
                            }
                          }}
                        >
                          {child.localizedCategoryName}
                        </a>
                      </li>
                    );
                  })}
                {showMoreButton && (
                  <li className={category.cssClassForHighlightedItem}>
                    <LinkButton
                      label="Mehr ..."
                      onClick={(): void => setExpandedCategory(category)}
                      className="text-bodySmall font-regular"
                      aria-describedby={`category-${category.categoryId}`}
                    />
                  </li>
                )}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default CategoryTreeList;
